import { depositNairaValue } from "../../../utils/money";
import { formatAmount } from "../../../utils/wordFunctions";

/**
 * The Activity screen reads two different endpoints:
 *
 *  • merchant-wide transactions — payouts and crypto deposits
 *  • deposits for a single payment page
 *
 * They return different field names for the same concepts, which is why the
 * old table was a wall of nested ternaries inside the render. Normalise once,
 * here, so the column definitions stay declarative.
 */
export type ActivityRow = {
  id: string;
  date?: string;
  type: "Payout" | "Deposit";
  description: string;
  amountLabel: string;
  usdLabel?: string;
  nairaLabel: string;
  status: string;
  raw: any;
};

export const normalizeActivity = (
  data: any[],
  isPageDeposits: boolean
): ActivityRow[] =>
  (data ?? []).map((item, index) => {
    if (isPageDeposits) {
      return {
        id: item?.txid ?? item?._id ?? String(index),
        date: item?.processedAt ?? item?.createdAt,
        type: "Deposit",
        description: item?.toAddress ?? "—",
        amountLabel: `${formatAmount(item?.amount)} ${(item?.currency ?? "").toUpperCase()}`,
        usdLabel: `$${formatAmount(item?.usdtAmount)}`,
        nairaLabel: `${formatAmount(depositNairaValue(item))} NGN`,
        status: "COMPLETED",
        raw: item,
      };
    }

    const isPayout = item?.transactionType === "payouts";

    if (isPayout) {
      return {
        id: item?._id ?? String(index),
        date: item?.processedAt ?? item?.createdAt,
        type: "Payout",
        description:
          item?.recipient?.accountName ??
          item?.recipient?.bankName ??
          "Payout",
        amountLabel: `${formatAmount(item?.asset?.amount)} ${(
          item?.asset?.name ?? ""
        ).toUpperCase()}`,
        usdLabel: item?.asset?.amountInUSD
          ? `$${formatAmount(item.asset.amountInUSD)}`
          : undefined,
        nairaLabel: `${formatAmount(item?.payoutAmount)} NGN`,
        status: item?.status ?? "—",
        raw: item,
      };
    }

    // crypto-deposit
    return {
      id: item?._id ?? item?.txid ?? String(index),
      date: item?.processedAt ?? item?.createdAt,
      type: "Deposit",
      description: "Crypto deposit",
      amountLabel: `${formatAmount(item?.amount)} ${(item?.currency ?? "").toUpperCase()}`,
      usdLabel: item?.usdtAmount ? `$${formatAmount(item.usdtAmount)}` : undefined,
      nairaLabel: `${formatAmount(depositNairaValue(item))} NGN`,
      status: "COMPLETED",
      raw: item,
    };
  });
